// Presentation of validated revision evidence. Projection cleanliness, replay
// exactness, job verdicts and old source reports are not QA certification.
const HEX = /^[a-f0-9]{64}$/;
const GRADES = new Set(['PASS', 'CONDITIONAL', 'FAIL']);
export function revisionQaKey(jobId, session, context = '') {
  return session?.revision && HEX.test(session.revision_digest || '')
    ? JSON.stringify([context, jobId, session.revision, session.revision_digest, session.replay_status]) : null;
}
export function bindRevisionQa(session, envelope, receipt) {
  if (envelope?.revision !== session?.revision || envelope?.revision_digest !== session?.revision_digest || envelope?.replay_status !== session?.replay_status
    || !HEX.test(envelope?.html_digest || '') || envelope.html_digest !== session?.projection?.html_digest
    || receipt?.schema !== 'QaReceipt.v1' || receipt.html_digest !== envelope.html_digest
    || receipt.engine !== 'floor_gate' || !GRADES.has(receipt.verdict)
    || !receipt.axes || typeof receipt.axes !== 'object' || Array.isArray(receipt.axes) || !Object.keys(receipt.axes).length
    || (receipt.unmeasured != null && !Array.isArray(receipt.unmeasured))
    || (receipt.stale_expensive != null && (!Array.isArray(receipt.stale_expensive) || receipt.stale_expensive.some(x => typeof x !== 'string')))) {
    throw new Error('QA evidence does not match this revision.');
  }
  return receipt;
}
export function revisionQaView(key, session, load, { dirty = false, headMoved = false, refreshing = false } = {}) {
  const base = { revision: session?.revision || null, receipt: null, state: 'unavailable', label: 'QA unavailable' };
  if (!key || !session) return base;
  if (refreshing) return { ...base, state: 'loading', label: 'Checking revision' };
  if (session.replay_status !== 'exact') return { ...base, state: 'pending', label: 'QA recheck required',
    receipt: load?.key === key && load.state === 'ready' ? load.receipt : null };
  if (!session.artifacts?.qa_receipt) return { ...base, state: 'ungraded', label: 'QA unmeasured' };
  if (load?.key !== key || load.state === 'loading') return { ...base, state: 'loading', label: 'Loading QA' };
  if (load.state !== 'ready') return { ...base, state: 'error', label: 'QA unavailable', error: load.error };
  if (dirty || headMoved) return { ...base, state: 'stale', label: dirty ? 'Draft · QA stale' : 'New head · QA stale', receipt: load.receipt };
  return { ...base, state: 'current', label: 'QA ' + load.receipt.verdict, receipt: load.receipt };
}
export function revisionQaPresentation(view) {
  // A retained PASS may still be present on a stale/pending view. Only current,
  // validated evidence is allowed to paint the shell as a passing/failing build.
  if (view?.state === 'current') {
    if (view.receipt?.verdict === 'PASS') return { tone: 'pass', icon: 'check' };
    if (view.receipt?.verdict === 'FAIL') return { tone: 'fail', icon: 'x' };
    if (view.receipt?.verdict === 'CONDITIONAL') return { tone: 'conditional', icon: 'warn' };
  }
  if (view?.state === 'stale' || view?.state === 'pending') return { tone: 'conditional', icon: 'warn' };
  return { tone: 'idle', icon: view?.state === 'loading' ? 'clock' : 'minus' };
}
export function revisionQaNeedsRun(view) {
  // Loading/stale evidence must resolve or be applied first; an original build
  // PASS is never a substitute for missing or invalid saved-revision QA.
  return ['ungraded', 'pending', 'error'].includes(view?.state);
}
export function revisionCertifyAllowed(session) {
  if (!session) return false;
  // Temporary FE mirror of server/routes/amendments.js:431-438. WP-21 will
  // expose the route's predicate; an explicit server capability always wins.
  if (Object.hasOwn(session.capabilities || {}, 'certify')) return session.capabilities.certify === true;
  return session.replay_status === 'pending' || (session.replay_status === 'exact'
    && session.revision === 'r0' && session.authority_mode === 'contract_exact' && !session.artifacts?.qa_receipt);
}
export function reviewBlockCounts(blocks = []) {
  return blocks.reduce((counts, block) => {
    const grade = block.qa === 'major' ? 'fail' : block.qa === 'pass' || block.qa === 'minor' ? block.qa : 'ungraded';
    counts[grade]++;
    return counts;
  }, { pass: 0, minor: 0, fail: 0, ungraded: 0 });
}
export async function fetchRevisionQa(api, jobId, session) {
  const [envelope, receipt] = await Promise.all([api.readRevision(jobId, session.revision), api.readRevisionQa(jobId, session.revision)]);
  return bindRevisionQa(session, envelope, receipt);
}
export function projectionDiagnostic(source, findings = []) {
  // These are projection diagnostics, not measured section-grade evidence.
  // A resolved finding / clean projection does not imply a QA pass.
  const rank = finding => {
    const tag = String(finding?.tag || finding?.severity || finding?.level || finding?.status || '').toLowerCase();
    if (/^(major|error|fail|blocked|critical|high|severe)$/.test(tag)) return 2;
    if (/^(minor|warn|warning|attention|medium|low|info)$/.test(tag)) return 1;
    return 0;
  };
  const worst = findings.reduce((a, b) => rank(b) > rank(a) ? b : a, null);
  const severity = Math.max(rank(worst), source?.review_state === 'blocked' ? 2 : source?.review_state === 'attention' ? 1 : 0);
  return { qa: severity === 2 ? 'major' : severity === 1 ? 'minor' : null,
    note: worst?.note || worst?.message || worst?.detail || worst?.summary || null };
}
export function historyQaLabel(revision, isHead) {
  if (revision?.evidence_scope === 'receipt_only') return revision.qa_receipt_available
    ? 'QA receipt (' + revision.revision + ') · checked on open' : 'QA receipt unavailable';
  if (revision?.replay_status === 'pending') return 'pending re-verification';
  if (revision?.qa_receipt_available === true) return (isHead ? 'Deterministic QA (' : 'Historical QA (') + revision.revision + ')';
  return revision?.replay_status === 'pending' ? 'pending re-verification'
    : revision?.replay_status === 'exact' ? 'Exact replay · QA unmeasured' : 'QA unmeasured';
}
export function historyQaAvailable(revision, session, isHead) {
  if (typeof revision?.qa_receipt_available === 'boolean') return revision.qa_receipt_available;
  return !!(isHead && session?.revision === revision?.revision && session?.artifacts?.qa_receipt);
}
if (typeof window !== 'undefined') window.GooseReviewQa = { historyQaLabel, historyQaAvailable };
